<template>
    <div class="earn-vault-modal-overlay" @click.self="$emit('close')">
        <div class="earn-vault-modal compass-card">
            <!-- Hidden wallet modal trigger -->
            <WalletModalProvider ref="walletModalProvider">
                <template #default="{ openModal }">
                    <div @click="openModal" ref="walletModalTrigger" style="display:none"></div>
                </template>
            </WalletModalProvider>

            <!-- Header -->
            <div class="d-flex justify-content-between align-items-center mb-3">
                <div class="d-flex align-items-center gap-2">
                    <img v-if="vault.token_logo"
                         :src="vault.token_logo"
                         :alt="vault.token_symbol"
                         style="width: 28px; height: 28px; border-radius: 50%;"
                         @error="$event.target.style.display='none'"
                    >
                    <div v-else class="token-icon-fallback" style="width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.7rem; background: linear-gradient(135deg, var(--compass-primary), var(--compass-accent)); color: #fff;">
                        {{ (vault.token_symbol || '?').slice(0, 3) }}
                    </div>
                    <h5 class="mb-0" style="font-weight: 600;">
                        {{ isWithdraw ? 'Withdraw' : 'Deposit' }} {{ vault.token_symbol }}
                    </h5>
                </div>
                <button class="btn-close" @click="$emit('close')"></button>
            </div>

            <!-- Vault Info -->
            <div class="d-flex justify-content-between align-items-center mb-3 p-2"
                 style="background: rgba(139, 92, 246, 0.05); border-radius: 8px;">
                <div>
                    <span class="compass-badge compass-badge--outline" style="font-size: 0.65rem; padding: 2px 6px;">
                        {{ capitalize(vault.protocol) }}
                    </span>
                    <span class="ms-1" style="font-weight: 500; font-size: 0.85rem;">
                        {{ vault.vault_name || vault.token_symbol + ' Vault' }}
                    </span>
                </div>
                <div class="text-end">
                    <span class="font-mono-num" style="color: var(--compass-success); font-weight: 600;">
                        {{ formatApy(vault.apy) }} APY
                    </span>
                </div>
            </div>

            <!-- Position info (withdraw mode) -->
            <div v-if="isWithdraw && position" class="mb-3 p-2"
                 style="background: rgba(34, 197, 94, 0.05); border-radius: 8px; border: 1px solid rgba(34, 197, 94, 0.2);">
                <div style="font-size: 0.85rem; color: #6b7280;">Current Position</div>
                <div class="font-mono-num" style="font-weight: 600;">
                    {{ formatAmount(position.depositedAmount) }} {{ vault.token_symbol }}
                </div>
            </div>

            <!-- Balance display (deposit mode) -->
            <div v-if="!isWithdraw && isConnected" class="mb-2 d-flex justify-content-between align-items-center"
                 style="font-size: 0.85rem;">
                <span style="color: #6b7280;">Wallet Balance</span>
                <span class="font-mono-num" style="font-weight: 500;">
                    <template v-if="loadingBalance">
                        <span class="spinner-border spinner-border-sm"></span>
                    </template>
                    <template v-else>
                        {{ formatAmount(walletBalance) }} {{ vault.token_symbol }}
                    </template>
                </span>
            </div>

            <!-- Amount Input -->
            <div class="mb-3">
                <label class="form-label" style="font-size: 0.85rem; font-weight: 500;">
                    {{ isWithdraw ? 'Withdraw Amount' : 'Deposit Amount' }}
                </label>
                <div class="input-group">
                    <input type="number" class="form-control font-mono-num"
                           v-model.number="amount"
                           :placeholder="'0.00 ' + vault.token_symbol"
                           step="any" min="0">
                    <span class="input-group-text" style="font-size: 0.85rem;">{{ vault.token_symbol }}</span>
                </div>
                <div class="d-flex gap-2 mt-2">
                    <button v-if="isWithdraw && position"
                            class="compass-btn compass-btn--sm compass-btn--outline"
                            @click="amount = position.depositedAmount">
                        Max ({{ formatAmount(position.depositedAmount) }})
                    </button>
                    <template v-else-if="walletBalance > 0">
                        <button class="compass-btn compass-btn--sm compass-btn--outline"
                                @click="amount = walletBalance * 0.5">50%</button>
                        <button class="compass-btn compass-btn--sm compass-btn--outline"
                                @click="amount = walletBalance">Max</button>
                    </template>
                </div>
            </div>

            <!-- Error display -->
            <div v-if="error" class="compass-info-box compass-info-box--danger mb-3" style="padding: 0.75rem;">
                <div style="font-size: 0.85rem;">{{ error }}</div>
            </div>

            <!-- Success display -->
            <div v-if="txSignature" class="compass-info-box compass-info-box--success mb-3" style="padding: 0.75rem;">
                <div style="font-size: 0.85rem;">
                    Transaction confirmed!
                    <a :href="explorerUrl" target="_blank" rel="noopener" style="color: var(--compass-success);">
                        View on Explorer <i class="fas fa-external-link-alt"></i>
                    </a>
                </div>
            </div>

            <!-- Mode Toggle -->
            <div class="analytics-tabs mb-3">
                <button class="tab-btn" :class="{ active: !isWithdraw }"
                        @click="switchMode('deposit')">Deposit</button>
                <button class="tab-btn" :class="{ active: isWithdraw }"
                        @click="switchMode('withdraw')">Withdraw</button>
            </div>

            <!-- Submit -->
            <button class="compass-btn compass-btn--primary w-100"
                    :disabled="processing || (isConnected && !canSubmit)"
                    @click="isConnected ? submit() : openWalletModal()">
                <span v-if="processing">
                    <span class="spinner-border spinner-border-sm me-1"></span>
                    {{ isWithdraw ? 'Withdrawing...' : 'Depositing...' }}
                </span>
                <span v-else-if="!isConnected">
                    <i class="fas fa-wallet me-1"></i> Connect Wallet
                </span>
                <span v-else>
                    {{ isWithdraw ? 'Withdraw' : 'Deposit' }} {{ vault.token_symbol }}
                </span>
            </button>

            <p v-if="isConnected" class="mt-2 mb-0 text-center" style="font-size: 0.75rem; color: #9ca3af;">
                Transaction will be signed by your connected wallet.
            </p>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { WalletModalProvider, useWallet } from 'solana-wallets-vue';
import { useSolSdk } from '../composables/useSolSdk';
import { useLendFormatters } from '../composables/useLendFormatters';

export default {
    name: 'EarnVaultDeposit',
    components: { WalletModalProvider },
    props: {
        vault: { type: Object, required: true },
        mode: { type: String, default: 'deposit' },
        position: { type: Object, default: null },
    },
    emits: ['close', 'success'],
    setup(props, { emit }) {
        const { getSdk } = useSolSdk();
        const wallet = useWallet();
        const { capitalize, formatApy, formatAmount } = useLendFormatters();

        const walletModalTrigger = ref(null);
        const currentMode = ref(props.mode);
        const amount = ref(null);
        const processing = ref(false);
        const error = ref(null);
        const txSignature = ref(null);
        const walletBalance = ref(0);
        const loadingBalance = ref(false);

        const isWithdraw = computed(() => currentMode.value === 'withdraw');
        const isConnected = computed(() => wallet.connected?.value || false);

        const canSubmit = computed(() => {
            if (!isConnected.value) return false;
            if (!amount.value || amount.value <= 0) return false;
            if (!isWithdraw.value && walletBalance.value > 0 && amount.value > walletBalance.value) return false;
            if (isWithdraw.value && props.position && amount.value > props.position.depositedAmount) return false;
            return true;
        });

        const explorerUrl = computed(() => {
            if (!txSignature.value) return '';
            return `https://solscan.io/tx/${txSignature.value}`;
        });

        // Re-fetch balance when wallet connects
        watch(isConnected, (connected) => {
            if (connected) fetchBalance();
        });

        function switchMode(mode) {
            currentMode.value = mode;
            error.value = null;
            txSignature.value = null;
            amount.value = null;
        }

        function openWalletModal() {
            if (walletModalTrigger.value) {
                walletModalTrigger.value.click();
            }
        }

        async function fetchBalance() {
            if (!isConnected.value || isWithdraw.value) return;
            loadingBalance.value = true;
            try {
                const { Connection, PublicKey } = await import('@solana/web3.js');
                const rpcUrl = window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com';
                const connection = new Connection(rpcUrl);
                const pubkey = new PublicKey(wallet.publicKey.value.toBase58());

                if (props.vault.token_symbol === 'SOL') {
                    const balance = await connection.getBalance(pubkey);
                    walletBalance.value = Math.max(0, (balance / 1e9) - 0.01);
                } else if (props.vault.token_mint) {
                    const { getAssociatedTokenAddress } = await import('@solana/spl-token');
                    const mint = new PublicKey(props.vault.token_mint);
                    const ata = await getAssociatedTokenAddress(mint, pubkey);
                    try {
                        const accountInfo = await connection.getTokenAccountBalance(ata);
                        walletBalance.value = Number(accountInfo.value.uiAmount || 0);
                    } catch {
                        walletBalance.value = 0;
                    }
                }
            } catch (e) {
                console.error('[EarnVaultDeposit] Balance fetch failed:', e);
                walletBalance.value = 0;
            } finally {
                loadingBalance.value = false;
            }
        }

        async function submit() {
            if (!canSubmit.value || processing.value) return;

            processing.value = true;
            error.value = null;
            txSignature.value = null;

            try {
                const sdk = await getSdk();

                if (!wallet.publicKey?.value) {
                    error.value = 'Please connect your wallet first.';
                    return;
                }

                const walletName = 'browser';
                let result;

                if (isWithdraw.value) {
                    result = await sdk.earn.withdraw(
                        walletName,
                        props.vault.token_symbol,
                        amount.value,
                        props.vault.protocol
                    );
                } else {
                    result = await sdk.earn.deposit(
                        walletName,
                        props.vault.token_symbol,
                        amount.value,
                        props.vault.protocol,
                        props.vault.vault_address
                    );
                }

                txSignature.value = result?.signature || result?.tx || null;
                emit('success');
            } catch (e) {
                console.error('[EarnVaultDeposit] Transaction failed:', e);
                const msg = e?.message || 'Transaction failed. Please try again.';
                if (msg.includes('not found as a protocol') || msg.includes('No provider registered')) {
                    error.value = `${capitalize(props.vault.protocol)} deposits are not yet supported in the browser. This vault's SDK provider could not be initialized.`;
                } else if (msg.includes('getSigner is not a function')) {
                    error.value = 'Wallet signer error — please disconnect and reconnect your wallet, then try again.';
                } else {
                    error.value = msg;
                }
            } finally {
                processing.value = false;
            }
        }

        onMounted(() => {
            fetchBalance();
        });

        return {
            walletModalTrigger,
            currentMode,
            amount,
            processing,
            error,
            txSignature,
            walletBalance,
            loadingBalance,
            isWithdraw,
            isConnected,
            canSubmit,
            explorerUrl,
            capitalize,
            formatApy,
            formatAmount,
            switchMode,
            openWalletModal,
            submit,
        };
    },
};
</script>

<style scoped>
.earn-vault-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 1rem;
}

.earn-vault-modal {
    width: 100%;
    max-width: 480px;
    max-height: 90vh;
    overflow-y: auto;
}

.input-group .form-control {
    font-size: 1.1rem;
    padding: 0.6rem 0.75rem;
}
</style>
